(() => {
  const isTerrainoEditor = document.documentElement.classList.contains('shopify-design-mode');
  const shouldReduceTerrainoMotion = () => isTerrainoEditor || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const terrainoMotionDisposers = new WeakMap();
  const registerTerrainoMotionDisposer = (element, disposer) => {
    const disposers = terrainoMotionDisposers.get(element) || [];
    disposers.push(disposer);
    terrainoMotionDisposers.set(element, disposers);
  };
  const disposeTerrainoMotion = (scope) => {
    (scope?.querySelectorAll?.('[data-terraino-count],[data-terraino-marquee],[data-terraino-builder-carousel],[data-terraino-carousel-controls],[data-terraino-product-rail],[data-terraino-persistent-header],[data-terraino-step-media],[data-terraino-email-popup]') || []).forEach((element) => {
      terrainoMotionDisposers.get(element)?.forEach((dispose) => dispose());
      terrainoMotionDisposers.delete(element);
    });
  };
  const disposeTerrainoEmailPopups = () => {
    (document.querySelectorAll?.('[data-terraino-email-popup]') || []).forEach((popup) => {
      terrainoMotionDisposers.get(popup)?.forEach((dispose) => dispose());
      terrainoMotionDisposers.delete(popup);
    });
  };

  const initializeTerrainoEmailPopups = (scope = document) => {
    (scope?.querySelectorAll?.('[data-terraino-email-popup]') || []).forEach((popup) => {
      if (popup.dataset.terrainoEmailPopupReady === 'true') return;
      const closeButton = popup.querySelector('[data-terraino-email-popup-close]');

      if (isTerrainoEditor) {
        popup.dataset.terrainoEmailPopupReady = 'true';
        let previewDisposed = false;
        const disposePreview = () => {
          if (previewDisposed) return;
          previewDisposed = true;
          closeButton?.removeEventListener('click', closePreview);
          document.removeEventListener('keydown', onPreviewKeydown);
        };
        const closePreview = () => {
          popup.removeAttribute('open');
          disposePreview();
        };
        const onPreviewKeydown = (event) => {
          if (event.key === 'Escape' && popup.open) closePreview();
        };
        closeButton?.addEventListener('click', closePreview);
        document.addEventListener('keydown', onPreviewKeydown);
        registerTerrainoMotionDisposer(popup, () => {
          disposePreview();
          delete popup.dataset.terrainoEmailPopupReady;
        });
        return;
      }

      const requestedPage = popup.dataset.terrainoEmailPopupPages || 'all';
      const currentPage = popup.dataset.terrainoEmailPopupPageType || '';
      const pageEligible = requestedPage === 'all'
        || requestedPage === currentPage
        || (requestedPage === 'home' && currentPage === 'index');
      const requestedDevices = popup.dataset.terrainoEmailPopupDevices || 'all';
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const deviceEligible = requestedDevices === 'all'
        || (requestedDevices === 'mobile' && isMobile)
        || (requestedDevices === 'desktop' && !isMobile);
      if (!pageEligible || !deviceEligible) return;
      popup.dataset.terrainoEmailPopupReady = 'true';

      const version = popup.dataset.terrainoEmailPopupVersion || '1';
      const viewsKey = `terraino-email-popup:${version}:views`;
      const dismissalKey = `terraino-email-popup:${version}:dismissed`;
      const completionKey = `terraino-email-popup:${version}:completed`;
      const readStorage = (storage, key) => {
        try {
          return storage.getItem(key);
        } catch (error) {
          console.warn('Terraino email popup storage is unavailable.', error);
          return null;
        }
      };
      const writeStorage = (storage, key, value) => {
        try {
          storage.setItem(key, value);
          return true;
        } catch (error) {
          console.warn('Terraino email popup storage is unavailable.', error);
          return false;
        }
      };

      const formState = popup.querySelector('[data-terraino-email-popup-form-state]');
      const popupForm = popup.querySelector('.terraino-email-popup__form');
      const returnToInput = popupForm?.querySelector('input[name="return_to"]');
      const popupResultUrl = new URL(window.location.href);
      const returnMarker = popupResultUrl.searchParams.get('terraino_email_popup');
      const returnMarkerMatches = returnMarker === version;
      const success = returnMarkerMatches
        && formState?.dataset.terrainoEmailPopupFormSuccess === 'true';
      const hasErrors = returnMarkerMatches
        && formState?.dataset.terrainoEmailPopupFormErrors === 'true';
      const updatePopupReturnTo = () => {
        if (!returnToInput) return;
        const returnUrl = new URL(window.location.href);
        returnUrl.searchParams.set('terraino_email_popup', version);
        returnToInput.value = `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;
      };
      const consumePopupReturnMarker = () => {
        const successUrl = new URL(window.location.href);
        successUrl.searchParams.delete('terraino_email_popup');
        const relativeUrl = `${successUrl.pathname}${successUrl.search}${successUrl.hash}`;
        window.history.replaceState(window.history.state, '', relativeUrl);
      };
      const repeatDays = Math.max(1, Number.parseInt(popup.dataset.terrainoEmailPopupRepeatDays, 10) || 14);
      const dismissedAt = Number.parseInt(readStorage(localStorage, dismissalKey), 10);
      const dismissalIsCurrent = Number.isFinite(dismissedAt)
        && Date.now() - dismissedAt < repeatDays * 24 * 60 * 60 * 1000;
      if (!success && !hasErrors && (readStorage(localStorage, completionKey) === 'true' || dismissalIsCurrent)) return;

      let eligibleViews = Number.parseInt(readStorage(sessionStorage, viewsKey), 10) || 0;
      if (popup.dataset.terrainoEmailPopupPageViewed !== 'true') {
        eligibleViews += 1;
        popup.dataset.terrainoEmailPopupPageViewed = 'true';
        writeStorage(sessionStorage, viewsKey, String(eligibleViews));
      }
      const minimumPageViews = Math.max(1, Number.parseInt(popup.dataset.terrainoEmailPopupMinimumPageViews, 10) || 1);
      if (!success && !hasErrors && eligibleViews < minimumPageViews) return;

      const trigger = popup.dataset.terrainoEmailPopupTrigger || 'delay';
      const effectiveTrigger = trigger === 'exit-intent' && isMobile ? 'delay' : trigger;
      const closeOnBackdrop = popup.dataset.terrainoEmailPopupCloseOnBackdrop === 'true';
      const input = popup.querySelector('input[type="email"]');
      const errorPanel = popup.querySelector('[data-terraino-email-popup-errors]');
      const offerPanel = popup.querySelector('[data-terraino-email-popup-offer-panel]');
      const successPanel = popup.querySelector('[data-terraino-email-popup-success-panel]');
      let delayTimer;
      let scrollListening = false;
      let exitListening = false;
      let completed = success;
      let disposed = false;
      let fallbackOpen = false;
      let previousFocus;

      const onScroll = () => {
        const scrollable = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
        const percentage = Math.min(100, Math.max(0, (window.scrollY / scrollable) * 100));
        const threshold = Number.parseInt(popup.dataset.terrainoEmailPopupScrollPercent, 10) || 50;
        if (percentage >= threshold) show();
      };
      const onExitIntent = (event) => {
        if (event.relatedTarget == null && event.clientY <= 0) show();
      };
      const clearTrigger = () => {
        clearTimeout(delayTimer);
        if (scrollListening) window.removeEventListener('scroll', onScroll);
        if (exitListening) document.removeEventListener('mouseout', onExitIntent);
        scrollListening = false;
        exitListening = false;
      };
      const show = () => {
        if (disposed || popup.open) return;
        clearTrigger();
        previousFocus = document.activeElement;
        if (typeof popup.showModal === 'function') {
          popup.showModal();
        } else {
          fallbackOpen = true;
          popup.setAttribute('open', '');
        }
        input?.focus();
      };
      const dismiss = () => {
        if (typeof popup.close === 'function') popup.close('dismissed');
        else {
          fallbackOpen = false;
          popup.removeAttribute('open');
          onClose();
        }
      };
      const onCloseButton = () => dismiss();
      const onCancel = () => {
        completed = false;
      };
      const onBackdropClick = (event) => {
        if (closeOnBackdrop && event.target === popup) dismiss();
      };
      const onDialogKeydown = (event) => {
        if (fallbackOpen && event.key === 'Escape') {
          event.preventDefault();
          dismiss();
        }
      };
      const cleanup = () => {
        if (disposed) return;
        disposed = true;
        clearTrigger();
        closeButton?.removeEventListener('click', onCloseButton);
        popup.removeEventListener('cancel', onCancel);
        popup.removeEventListener('click', onBackdropClick);
        popup.removeEventListener('close', onClose);
        document.removeEventListener('keydown', onDialogKeydown);
        popupForm?.removeEventListener('submit', updatePopupReturnTo);
        delete popup.dataset.terrainoEmailPopupReady;
      };
      const onClose = () => {
        if (!completed) writeStorage(localStorage, dismissalKey, String(Date.now()));
        previousFocus?.focus();
        cleanup();
      };

      closeButton?.addEventListener('click', onCloseButton);
      popup.addEventListener('cancel', onCancel);
      popup.addEventListener('click', onBackdropClick);
      popup.addEventListener('close', onClose);
      document.addEventListener('keydown', onDialogKeydown);
      popupForm?.addEventListener('submit', updatePopupReturnTo);
      registerTerrainoMotionDisposer(popup, cleanup);

      if (success) {
        completed = true;
        offerPanel.hidden = true;
        successPanel.hidden = false;
        popup.setAttribute('aria-labelledby', 'TerrainoEmailPopupSuccessTitle');
        popup.setAttribute('aria-describedby', 'TerrainoEmailPopupSuccessText');
        writeStorage(localStorage, completionKey, 'true');
        consumePopupReturnMarker();
        show();
        return;
      }

      if (hasErrors) {
        errorPanel.hidden = false;
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', [input.getAttribute('aria-describedby'), errorPanel.id].filter(Boolean).join(' '));
        show();
        return;
      }

      if (effectiveTrigger === 'delay') {
        const delaySeconds = Math.max(0, Number.parseInt(popup.dataset.terrainoEmailPopupDelaySeconds, 10) || 0);
        delayTimer = window.setTimeout(show, delaySeconds * 1000);
      } else if (effectiveTrigger === 'scroll') {
        scrollListening = true;
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      } else if (effectiveTrigger === 'exit-intent') {
        exitListening = true;
        document.addEventListener('mouseout', onExitIntent);
      }
    });
  };

  const initializeTerrainoCount = (scope = document) => {
    (scope?.querySelectorAll?.('[data-terraino-count]') || []).forEach((counter) => {
      if (counter.dataset.terrainoCountReady === 'true') return;
      counter.dataset.terrainoCountReady = 'true';

      let current = Number.parseInt(counter.dataset.terrainoCountStart, 10) || 0;
      const interval = Number.parseInt(counter.dataset.terrainoCountInterval, 10) || 1;
      const variance = Number.parseInt(counter.dataset.terrainoCountVariance, 10) || 0;
      const render = (value) => { counter.textContent = new Intl.NumberFormat().format(value); };
      const delay = () => Math.max(1000, Math.round((interval + (variance ? (Math.random() * 2 - 1) * variance : 0)) * 1000));
      let timer;
      let observer;
      let disposed = false;
      const scheduleNext = () => {
        if (disposed) return;
        render(current += 1);
        timer = window.setTimeout(scheduleNext, delay());
      };
      render(current);
      registerTerrainoMotionDisposer(counter, () => {
        disposed = true;
        clearTimeout(timer);
        observer?.disconnect();
      });

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const start = () => { timer = window.setTimeout(scheduleNext, delay()); };
      if (!('IntersectionObserver' in window)) {
        start();
        return;
      }
      observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        start();
      }, { threshold: 0.35 });
      observer.observe(counter);
    });
  };

  const initializeTerrainoBuilderCarousels = (scope = document) => {
    (scope?.querySelectorAll?.('[data-terraino-builder-carousel]') || []).forEach((rail) => {
      if (rail.dataset.terrainoBuilderCarouselReady === 'true') return;
      rail.dataset.terrainoBuilderCarouselReady = 'true';

      const cards = [...rail.querySelectorAll('.terraino-builder-tools__card')];
      const controls = rail.parentElement?.querySelector('.terraino-builder-tools__controls');
      const next = controls?.querySelector('[data-terraino-builder-next]');
      const reduceMotion = shouldReduceTerrainoMotion();
      const autoAdvance = rail.dataset.terrainoBuilderAutoplay === 'true' && !reduceMotion;
      const pauseDuration = Math.max(1000, Number.parseFloat(rail.dataset.terrainoBuilderPause) * 1000 || 3000);
      const pauseOnHover = rail.dataset.terrainoBuilderPauseHover === 'true';
      let timer;
      let hoverPaused = false;
      let stopped = false;
      let disposed = false;

      const clearTimer = () => {
        window.clearTimeout(timer);
        timer = undefined;
      };
      const step = () => {
        const firstCard = cards[0];
        const gap = Number.parseFloat(window.getComputedStyle(rail).gap) || 0;
        return (firstCard?.getBoundingClientRect().width || rail.clientWidth) + gap;
      };
      const advance = () => {
        if (disposed || stopped) return;
        const atEnd = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 2;
        if (atEnd) {
          rail.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          rail.scrollBy({ left: step(), behavior: 'smooth' });
        }
      };
      const schedule = () => {
        clearTimer();
        if (!autoAdvance || stopped || hoverPaused || rail.scrollWidth <= rail.clientWidth + 1) return;
        timer = window.setTimeout(() => {
          advance();
          schedule();
        }, pauseDuration);
      };
      const stop = () => {
        stopped = true;
        clearTimer();
      };
      const onPointerDown = () => stop();
      const onTouchStart = () => stop();
      const onWheel = () => stop();
      const onPointerEnter = () => {
        if (!pauseOnHover) return;
        hoverPaused = true;
        clearTimer();
      };
      const onPointerLeave = () => {
        if (!pauseOnHover) return;
        hoverPaused = false;
        schedule();
      };
      const onNext = () => {
        stop();
        const atEnd = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 2;
        if (atEnd) rail.scrollTo({ left: 0, behavior: 'smooth' });
        else rail.scrollBy({ left: step(), behavior: 'smooth' });
      };

      rail.addEventListener('pointerdown', onPointerDown, { passive: true });
      rail.addEventListener('touchstart', onTouchStart, { passive: true });
      rail.addEventListener('wheel', onWheel, { passive: true });
      rail.addEventListener('pointerenter', onPointerEnter);
      rail.addEventListener('pointerleave', onPointerLeave);
      next?.addEventListener('click', onNext);
      registerTerrainoMotionDisposer(rail, () => {
        disposed = true;
        clearTimer();
        rail.removeEventListener('pointerdown', onPointerDown);
        rail.removeEventListener('touchstart', onTouchStart);
        rail.removeEventListener('wheel', onWheel);
        rail.removeEventListener('pointerenter', onPointerEnter);
        rail.removeEventListener('pointerleave', onPointerLeave);
        next?.removeEventListener('click', onNext);
      });
      schedule();
    });
  };

  const initializeTerrainoMarquees = (scope = document) => {
    (scope?.querySelectorAll?.('[data-terraino-marquee]') || []).forEach((rail) => {
      if (rail.dataset.terrainoMarqueeReady === 'true') return;
      rail.dataset.terrainoMarqueeReady = 'true';

      const sourceGroup = rail.querySelector('[data-terraino-marquee-group]');
      const reduceMotion = shouldReduceTerrainoMotion();
      const autoScroll = rail.dataset.terrainoMarqueeAutoplay === 'true' && !reduceMotion;
      const pauseOnHover = rail.dataset.terrainoMarqueePause === 'true';
      const speed = Number.parseFloat(rail.dataset.terrainoMarqueeSpeed) || 25;
      let paused = false;
      let railHovered = false;
      let railFocused = false;
      let controlsHovered = false;
      let controlsFocused = false;
      let holdDirection = 0;
      let lastTime = 0;
      let scrollPosition = rail.scrollLeft;
      let frame;
      let disposed = false;

      const loopWidth = () => {
        if (!sourceGroup) return 0;
        const gap = Number.parseFloat(window.getComputedStyle(rail).gap) || 0;
        return sourceGroup.getBoundingClientRect().width + gap;
      };

      const normalize = () => {
        const width = loopWidth();
        if (!width) return;
        if (scrollPosition >= width) scrollPosition -= width;
        if (scrollPosition < 0) scrollPosition += width;
        rail.scrollLeft = scrollPosition;
      };

      const syncPaused = () => {
        paused = Boolean(holdDirection || railHovered || railFocused || controlsHovered || controlsFocused);
      };
      const onPointerEnter = () => { railHovered = true; syncPaused(); };
      const onPointerLeave = () => { railHovered = false; syncPaused(); };
      const onFocusIn = () => { railFocused = true; syncPaused(); };
      const onFocusOut = () => { railFocused = false; syncPaused(); };
      const onHoldStart = (event) => {
        const direction = Number(event.detail?.direction);
        if (!Number.isFinite(direction) || !direction) return;
        holdDirection = direction;
        syncPaused();
        event.preventDefault();
      };
      const onHoldStop = (event) => {
        if (!holdDirection) return;
        holdDirection = 0;
        syncPaused();
        lastTime = 0;
        event.preventDefault();
      };
      const onScroll = () => {
        if (Math.abs(rail.scrollLeft - scrollPosition) > 1) scrollPosition = rail.scrollLeft;
        normalize();
      };
      const onCarouselControl = (event) => {
        const direction = Number(event.detail?.direction);
        const step = Number(event.detail?.step);
        if (!Number.isFinite(direction) || !Number.isFinite(step)) return;
        event.preventDefault();
        scrollPosition += direction * step;
        normalize();
      };
      if (pauseOnHover) {
        rail.addEventListener('pointerenter', onPointerEnter);
        rail.addEventListener('pointerleave', onPointerLeave);
        rail.addEventListener('focusin', onFocusIn);
        rail.addEventListener('focusout', onFocusOut);
      }
      rail.addEventListener('scroll', onScroll, { passive: true });
      rail.addEventListener('terraino:carousel-control', onCarouselControl);
      rail.addEventListener('terraino:carousel-hold-start', onHoldStart);
      rail.addEventListener('terraino:carousel-hold-stop', onHoldStop);
      const controls = [...(document.querySelectorAll?.('[data-terraino-carousel-controls]') || [])]
        .find((candidate) => candidate.querySelector?.(`[aria-controls="${rail.id}"]`));
      const onControlsEnter = () => { if (pauseOnHover) { controlsHovered = true; syncPaused(); } };
      const onControlsLeave = () => { if (pauseOnHover) { controlsHovered = false; syncPaused(); } };
      const onControlsFocusIn = () => { if (pauseOnHover) { controlsFocused = true; syncPaused(); } };
      const onControlsFocusOut = (event) => {
        if (pauseOnHover && !controls?.contains?.(event.relatedTarget)) { controlsFocused = false; syncPaused(); }
      };
      if (controls && pauseOnHover) {
        controls.addEventListener('pointerenter', onControlsEnter);
        controls.addEventListener('pointerleave', onControlsLeave);
        controls.addEventListener('focusin', onControlsFocusIn);
        controls.addEventListener('focusout', onControlsFocusOut);
      }
      registerTerrainoMotionDisposer(rail, () => {
        disposed = true;
        cancelAnimationFrame(frame);
        rail.removeEventListener('scroll', onScroll);
        rail.removeEventListener('terraino:carousel-control', onCarouselControl);
        rail.removeEventListener('terraino:carousel-hold-start', onHoldStart);
        rail.removeEventListener('terraino:carousel-hold-stop', onHoldStop);
        if (controls && pauseOnHover) {
          controls.removeEventListener('pointerenter', onControlsEnter);
          controls.removeEventListener('pointerleave', onControlsLeave);
          controls.removeEventListener('focusin', onControlsFocusIn);
          controls.removeEventListener('focusout', onControlsFocusOut);
        }
        if (pauseOnHover) {
          rail.removeEventListener('pointerenter', onPointerEnter);
          rail.removeEventListener('pointerleave', onPointerLeave);
          rail.removeEventListener('focusin', onFocusIn);
          rail.removeEventListener('focusout', onFocusOut);
        }
      });
      if (!sourceGroup) return;

      const tick = (now) => {
        if (disposed) return;
        if (lastTime && (holdDirection || (autoScroll && !paused))) {
          if (Math.abs(rail.scrollLeft - scrollPosition) > 1) scrollPosition = rail.scrollLeft;
          scrollPosition += (holdDirection || 1) * speed * ((now - lastTime) / 1000);
          normalize();
        }
        lastTime = now;
        frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    });
  };

  const initializeTerrainoCarouselControls = (scope = document) => {
    (scope?.querySelectorAll?.('[data-terraino-carousel-controls]') || []).forEach((controls) => {
      if (controls.dataset.terrainoCarouselReady === 'true') return;
      const firstButton = controls.querySelector('[aria-controls]');
      const rail = document.getElementById(firstButton?.getAttribute('aria-controls'));
      if (!rail) return;
      controls.dataset.terrainoCarouselReady = 'true';
      const previous = controls.querySelector('[data-terraino-carousel-prev]');
      const next = controls.querySelector('[data-terraino-carousel-next]');
      let holdTimer;
      let activeHold;
      let holdTriggered = false;
      let suppressClick = false;
      const stopHold = () => {
        clearTimeout(holdTimer);
        holdTimer = undefined;
        if (!activeHold) return;
        if (holdTriggered) {
          suppressClick = true;
          rail.dispatchEvent(new CustomEvent('terraino:carousel-hold-stop', { cancelable: true }));
        }
        activeHold = undefined;
        holdTriggered = false;
      };
      const onControlPointerDown = (event) => {
        const control = event.target.closest?.('[data-terraino-carousel-prev],[data-terraino-carousel-next]');
        if (!control || !rail.matches?.('[data-terraino-marquee]') || event.button !== 0) return;
        suppressClick = false;
        const direction = control.matches('[data-terraino-carousel-next]') ? 1 : -1;
        const firstCard = rail.querySelector('[data-terraino-marquee-card]') || rail.firstElementChild;
        const gap = Number.parseFloat(window.getComputedStyle(rail).gap) || 0;
        const step = (firstCard?.getBoundingClientRect().width || rail.clientWidth) + gap;
        stopHold();
        activeHold = { direction, step };
        holdTimer = setTimeout(() => {
          if (!activeHold) return;
          holdTriggered = true;
          rail.dispatchEvent(new CustomEvent('terraino:carousel-hold-start', {
            cancelable: true,
            detail: activeHold,
          }));
        }, 300);
      };
      const onDocumentPointerEnd = () => stopHold();
      const onControlClick = (event) => {
        if (!suppressClick || event.detail === 0) return;
        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      };
      const update = () => {
        const hasOverflow = rail.scrollWidth > rail.clientWidth + 1;
        controls.hidden = !hasOverflow;
        if (!hasOverflow) return;
        previous.hidden = rail.scrollLeft <= 1;
        next.hidden = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 1;
      };
      rail.addEventListener('scroll', update, { passive: true });
      controls.addEventListener('pointerdown', onControlPointerDown);
      controls.addEventListener('click', onControlClick, true);
      document.addEventListener('pointerup', onDocumentPointerEnd);
      document.addEventListener('pointercancel', onDocumentPointerEnd);
      let observer;
      if ('ResizeObserver' in window) {
        observer = new ResizeObserver(update);
        observer.observe(rail);
      }
      registerTerrainoMotionDisposer(controls, () => {
        stopHold();
        rail.removeEventListener('scroll', update);
        controls.removeEventListener('pointerdown', onControlPointerDown);
        controls.removeEventListener('click', onControlClick, true);
        document.removeEventListener('pointerup', onDocumentPointerEnd);
        document.removeEventListener('pointercancel', onDocumentPointerEnd);
        observer?.disconnect();
      });
      update();
    });
  };

  const initializeTerrainoProductRails = (scope = document) => {
    (scope?.querySelectorAll?.('[data-terraino-product-rail]') || []).forEach((rail) => {
      if (rail.dataset.terrainoProductRailReady === 'true') return;
      rail.dataset.terrainoProductRailReady = 'true';
      const cards = [...rail.querySelectorAll('.terraino-product-scroller__card')];
      const viewAll = rail.parentElement.querySelector('[data-terraino-product-view-all]');
      const collectionCount = Number(rail.dataset.terrainoProductCount) || cards.length;
      const updateViewAll = (shownCount) => {
        if (viewAll) viewAll.hidden = collectionCount <= shownCount;
      };
      const update = () => {
        const isTouchLayout = window.matchMedia('(max-width:989px)').matches;
        if (isTouchLayout) {
          cards.forEach((card) => { card.hidden = false; });
          updateViewAll(cards.length);
          return;
        }
        cards.forEach((card) => { card.hidden = false; });
        const configuredCardWidth = Number.parseFloat(rail.dataset.terrainoProductCardWidth) || rail.clientWidth;
        const railStyle = window.getComputedStyle(rail);
        const gap = Number.parseFloat(railStyle.gap) || 0;
        const contentWidth = Math.max(0, rail.clientWidth - Number.parseFloat(railStyle.paddingLeft) - Number.parseFloat(railStyle.paddingRight));
        const visibleCount = Math.min(cards.length, Math.max(1, Math.floor((contentWidth + gap) / (configuredCardWidth + gap))));
        cards.forEach((card, index) => { card.hidden = index >= visibleCount; });
        rail.style.setProperty('--tps-visible-products', String(visibleCount));
        updateViewAll(visibleCount);
      };
      let settleFrame;
      const scheduleUpdate = () => {
        window.cancelAnimationFrame(settleFrame);
        settleFrame = window.requestAnimationFrame(() => {
          update();
          settleFrame = window.requestAnimationFrame(update);
        });
      };
      let observer;
      const touchLayout = window.matchMedia('(max-width:989px)');
      const onTouchLayoutChange = () => {
        if (touchLayout.matches) cards.forEach((card) => { card.hidden = false; });
        scheduleUpdate();
      };
      touchLayout.addEventListener?.('change', onTouchLayoutChange);
      if ('ResizeObserver' in window) {
        observer = new ResizeObserver(scheduleUpdate);
        observer.observe(rail);
      } else {
        window.addEventListener('resize', scheduleUpdate);
      }
      registerTerrainoMotionDisposer(rail, () => {
        observer?.disconnect();
        window.removeEventListener('resize', scheduleUpdate);
        touchLayout.removeEventListener?.('change', onTouchLayoutChange);
        window.cancelAnimationFrame(settleFrame);
      });
      scheduleUpdate();
    });
  };

  const initializeTerrainoStepMedia = (scope = document) => {
    (scope?.querySelectorAll?.('[data-terraino-step-media]') || []).forEach((media) => {
      if (media.dataset.terrainoStepMediaReady === 'true') return;
      media.dataset.terrainoStepMediaReady = 'true';

      const frames = [...media.querySelectorAll('[data-terraino-step-image]')];
      if (frames.length < 2 || shouldReduceTerrainoMotion()) return;

      const interval = Math.max(2000, Number.parseInt(media.dataset.terrainoStepCycleMs, 10) || 5000);
      let activeIndex = Math.max(0, frames.findIndex((frame) => frame.classList.contains('is-active')));
      let timer;
      let observer;
      let disposed = false;
      let started = false;

      const show = (index) => {
        frames.forEach((frame, frameIndex) => {
          const active = frameIndex === index;
          frame.classList.toggle('is-active', active);
          frame.setAttribute('aria-hidden', String(!active));
        });
      };
      const cycle = () => {
        if (disposed) return;
        activeIndex = (activeIndex + 1) % frames.length;
        show(activeIndex);
        timer = window.setTimeout(cycle, interval);
      };
      const start = () => {
        if (started || disposed) return;
        started = true;
        timer = window.setTimeout(cycle, interval);
      };

      registerTerrainoMotionDisposer(media, () => {
        disposed = true;
        clearTimeout(timer);
        observer?.disconnect();
      });

      if (!('IntersectionObserver' in window)) {
        start();
        return;
      }
      observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        start();
      }, { threshold: 0.35 });
      observer.observe(media);
    });
  };

  const initializeTerrainoPersistentHeaders = (scope = document) => {
    (scope?.querySelectorAll?.('[data-terraino-persistent-header]') || []).forEach((shell) => {
      if (shell.dataset.terrainoPersistentHeaderReady === 'true') return;
      const header = shell.querySelector('.terraino-header');
      if (!header) return;
      shell.dataset.terrainoPersistentHeaderReady = 'true';

      const headerTop = shell.getBoundingClientRect().top + window.scrollY;
      let frame;
      const setShellHeight = () => {
        shell.style.setProperty('--terraino-header-height', `${header.offsetHeight}px`);
      };

      const reset = () => {
        shell.classList.remove('terraino-header-shell--persistent');
        header.classList.remove('terraino-header--persistent');
      };
      const update = () => {
        frame = undefined;
        const scrollTop = window.scrollY;
        if (scrollTop >= headerTop) {
          setShellHeight();
          shell.classList.add('terraino-header-shell--persistent');
          header.classList.add('terraino-header--persistent');
        } else {
          reset();
        }
      };
      const onScroll = () => {
        if (frame == null) frame = requestAnimationFrame(update);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', setShellHeight);
      update();
      registerTerrainoMotionDisposer(shell, () => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', setShellHeight);
        cancelAnimationFrame(frame);
        reset();
        shell.style.removeProperty('--terraino-header-height');
      });
    });
  };

  let terrainoLooxOverlayObserver;
  const initializeTerrainoLooxOverlayClose = () => {
    if (terrainoLooxOverlayObserver || !document.body || typeof MutationObserver !== 'function') return;
    const addCloseControl = () => {
      const overlay = document.querySelector('#looxOverlay_quickview');
      if (!overlay || overlay.querySelector('[data-terraino-loox-overlay-close]')) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'terraino-loox-overlay-close terraino-type--action';
      button.dataset.terrainoLooxOverlayClose = 'true';
      button.setAttribute('aria-label', 'Close review');
      button.textContent = 'Close review';
      button.addEventListener('click', () => {
        overlay.remove();
        if (!document.querySelectorAll('.loox-overlay').length) document.body.style.removeProperty('overflow');
      }, { once: true });
      overlay.append(button);
    };
    terrainoLooxOverlayObserver = new MutationObserver(addCloseControl);
    terrainoLooxOverlayObserver.observe(document.body, { childList: true, subtree: true });
    addCloseControl();
  };

  const initializeTerrainoCorePrerequisites = (scope = document) => {
    (scope?.querySelectorAll?.('.terraino-core-prerequisite') || []).forEach((component) => {
      if (component.dataset.terrainoCorePrerequisiteReady === 'true') return;
      component.dataset.terrainoCorePrerequisiteReady = 'true';

      const errorElement = component.querySelector('[data-terraino-core-add-error]');
      const showError = (errorElement) => {
        if (!errorElement) return;
        errorElement.hidden = false;
        errorElement.textContent = 'Unable to add the selected Core bundle. Please try again.';
      };

      component.querySelectorAll('[data-terraino-core-add]').forEach((button) => {
        button.addEventListener('click', async (event) => {
          event.preventDefault();
          const variantId = button.dataset.terrainoCoreVariantId;
          const cartDrawer = button.closest('cart-drawer');

          if (errorElement) {
            errorElement.textContent = '';
            errorElement.hidden = true;
          }
          button.disabled = true;

          try {
            if (!variantId) throw new Error('Missing Core variant ID.');
            const response = await fetch(routes.cart_add_url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify({
                id: variantId,
                quantity: 1,
                sections: 'cart-drawer,cart-icon-bubble',
              }),
            });
            if (!response.ok) throw new Error(`Core add failed with status ${response.status}.`);

            const parsedState = await response.json();
            if (cartDrawer) {
              if (typeof cartDrawer.renderContents !== 'function') throw new Error('Cart drawer cannot render refreshed contents.');
              cartDrawer.renderContents(parsedState);
              initializeTerrainoCorePrerequisites(cartDrawer);
            } else {
              window.location.assign(routes.cart_url);
            }
          } catch (error) {
            console.error('Unable to add TERRAINO Core bundle.', error);
            showError(cartDrawer?.querySelector('[data-terraino-core-add-error]') || errorElement);
          } finally {
            button.disabled = false;
          }
        });
      });
    });
  };

  const initializeTerrainoMotion = (scope = document) => {
    initializeTerrainoCount(scope);
    initializeTerrainoStepMedia(scope);
    initializeTerrainoBuilderCarousels(scope);
    initializeTerrainoMarquees(scope);
    initializeTerrainoCarouselControls(scope);
    initializeTerrainoProductRails(scope);
    initializeTerrainoPersistentHeaders(scope);
    initializeTerrainoEmailPopups(scope);
  };

  document.addEventListener('DOMContentLoaded', () => {
    initializeTerrainoMotion();
    initializeTerrainoLooxOverlayClose();
    initializeTerrainoCorePrerequisites();
  });
  document.addEventListener('terraino:cart:render', (event) => initializeTerrainoCorePrerequisites(event.target));
  document.addEventListener('terraino:manual:append', (event) => {
    const article = event.detail;
    const stream = article instanceof Element
      ? article.parentElement?.closest('[data-terraino-manual-reader] [data-terraino-manual-stream]')
      : null;
    if (!(article instanceof Element) || !article.isConnected || !article.matches('article[data-terraino-manual-page]') || !stream) return;
    initializeTerrainoMotion(article);
  });
  document.addEventListener('shopify:section:load', (event) => {
    initializeTerrainoMotion(event.target);
    initializeTerrainoCorePrerequisites(event.target);
  });
  document.addEventListener('shopify:section:load', () => initializeTerrainoEmailPopups());
  document.addEventListener('shopify:section:unload', (event) => disposeTerrainoMotion(event.target));
  document.addEventListener('shopify:section:unload', () => {
    disposeTerrainoEmailPopups();
  });

  const terrainoDrawerColorSchemes = new Set([
    'background-1', 'background-1-gradient', 'background-2', 'background-2-gradient',
    'background-3', 'background-3-gradient', 'background-4', 'background-4-gradient',
    'accent-1', 'accent-1-gradient', 'accent-2', 'accent-2-gradient',
    'accent-3', 'accent-3-gradient', 'accent-4', 'accent-4-gradient',
  ]);

  const setTerrainoDrawerPanel = (drawer, panelName) => {
    let activePanel;
    drawer.querySelectorAll('[data-terraino-drawer-panel]').forEach((panel) => {
      const isActivePanel = panel.dataset.terrainoDrawerPanel === panelName;
      panel.hidden = !isActivePanel;
      if (isActivePanel) activePanel = panel;
    });
    drawer.querySelectorAll('[data-terraino-drawer-tab]').forEach((tab) => {
      tab.setAttribute('aria-selected', String(tab.dataset.terrainoDrawerTab === panelName));
    });

    const data = activePanel?.dataset;
    const colorScheme = data && terrainoDrawerColorSchemes.has(data.terrainoDrawerColorScheme)
      ? data.terrainoDrawerColorScheme
      : null;
    const drawerPanel = drawer.querySelector('.terraino-drawer__panel');
    if (drawerPanel && colorScheme) {
      terrainoDrawerColorSchemes.forEach((scheme) => drawerPanel.classList.remove(`terraino-tone--${scheme}`));
      drawerPanel.classList.add(`terraino-tone--${colorScheme}`);
      drawerPanel.style.cssText = activePanel.style.cssText;
    }
  };

  const terrainoDrawerOpeners = new WeakMap();
  const closeTerrainoDrawer = (drawer) => {
    if (!drawer || drawer.hidden) return;
    const opener = terrainoDrawerOpeners.get(drawer);
    const triggers = [...document.querySelectorAll(`[data-terraino-drawer-open][aria-controls="${drawer.id}"]`)];
    drawer.hidden = true;
    document.documentElement.classList.remove('terraino-drawer-open');
    triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
    terrainoDrawerOpeners.delete(drawer);
    const focusTarget = opener?.isConnected && opener.getClientRects().length
      ? opener : triggers.find((trigger) => trigger.getClientRects().length);
    focusTarget?.focus({ preventScroll: true });
  };

  document.addEventListener('click', (event) => {
    const drawerOpener = event.target.closest('[data-terraino-drawer-open]');
    if (drawerOpener) {
      const drawer = document.getElementById(drawerOpener.getAttribute('aria-controls'));
      if (!drawer) return;
      terrainoDrawerOpeners.set(drawer, drawerOpener);
      drawer.hidden = false;
      document.documentElement.classList.add('terraino-drawer-open');
      document.querySelectorAll(`[data-terraino-drawer-open][aria-controls="${drawer.id}"]`).forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(trigger === drawerOpener));
      });
      setTerrainoDrawerPanel(drawer, drawerOpener.dataset.terrainoDrawerOpen);
      drawer.querySelector('.terraino-drawer__panel [data-terraino-drawer-close]')?.focus({ preventScroll: true });
      return;
    }

    const drawerClose = event.target.closest('[data-terraino-drawer-close]');
    if (drawerClose) {
      closeTerrainoDrawer(drawerClose.closest('[data-terraino-drawer]'));
      return;
    }

    const drawerTab = event.target.closest('[data-terraino-drawer-tab]');
    if (drawerTab) {
      const drawer = drawerTab.closest('[data-terraino-drawer]');
      if (drawer) setTerrainoDrawerPanel(drawer, drawerTab.dataset.terrainoDrawerTab);
      return;
    }

    const trigger = event.target.closest('[data-terraino-menu]');
    if (trigger) {
      const target = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!target) return;
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!expanded));
      target.hidden = expanded;
      return;
    }

    const control = event.target.closest('[data-terraino-carousel-prev],[data-terraino-carousel-next]');
    if (!control) return;
    const rail = document.getElementById(control.getAttribute('aria-controls'));
    if (!rail) return;
    const direction = control.matches('[data-terraino-carousel-next]') ? 1 : -1;
    const firstCard = rail.querySelector('[data-terraino-marquee-card]') || rail.firstElementChild;
    const gap = Number.parseFloat(window.getComputedStyle(rail).gap) || 0;
    const step = (firstCard?.getBoundingClientRect().width || rail.clientWidth) + gap;
    const carouselControl = new CustomEvent('terraino:carousel-control', {
      cancelable: true,
      detail: { direction, step },
    });
    if (!rail.dispatchEvent(carouselControl)) return;
    rail.scrollBy({ left: step * direction, behavior: 'smooth' });
  });

  document.addEventListener('keydown', (event) => {
    if (event.defaultPrevented || document.querySelector('dialog:modal')) return;
    const drawer = document.querySelector('[data-terraino-drawer]:not([hidden])');
    if (!drawer) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeTerrainoDrawer(drawer);
      return;
    }
    if (event.key !== 'Tab') return;
    const panel = drawer.querySelector('.terraino-drawer__panel');
    if (!panel) return;
    const controls = [...panel.querySelectorAll('a[href],button,input,select,textarea,[tabindex]')]
      .filter((control) => !control.disabled && control.tabIndex >= 0 && control.getClientRects().length > 0);
    const first = controls[0];
    const last = controls[controls.length - 1];
    const active = document.activeElement;
    if (!first) {
      event.preventDefault();
      panel.focus({ preventScroll: true });
    } else if (event.shiftKey && (active === first || !controls.includes(active))) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && (active === last || !controls.includes(active))) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  });
})();
